import { useReportTheme } from "@/components/ui/report/ReportThemeProvider";
import {
  type ReportSectionPresentation,
  resolveReportSectionPresentation,
  resolveReportSpacingStyle,
} from "@/components/ui/report/reportPresentation";
import { reportScrollTargetProps } from "@/lib/template-studio/reportScrollTarget";
import { cn } from "@/lib/utils";

import { ArticleItem } from "./ArticleItem";
import { SectionHeader } from "./SectionHeader";

export interface ReportSectionArticle {
  headline: string;
  sourceName: string;
  sourceUrl: string;
  body: string;
}

export interface ReportSectionProps extends ReportSectionPresentation {
  title: string;
  articles: ReportSectionArticle[];
  /** Muted note shown between the section bar and its articles. */
  caption?: string;
  /** Spec element id for in-report scroll targets (injected at preview render). */
  scrollTargetKey?: string;
  className?: string;
}

/** Topic section with a label bar and one or more story blocks. */
export const ReportSection = ({
  title,
  articles,
  caption,
  scrollTargetKey,
  className,
  ...presentation
}: ReportSectionProps) => {
  const theme = useReportTheme();
  const {
    backgroundColor,
    labelTreatment,
    labelStyle,
    headlineStyle,
    bodyStyle,
    spacing,
  } = resolveReportSectionPresentation(theme?.reportSection, presentation);
  const hasVisibleArticles = articles.some(
    (article) => article.headline.trim().length > 0,
  );

  if (title.trim().length === 0 && !hasVisibleArticles) {
    return null;
  }

  return (
    <section
      className={cn("flex w-full flex-col gap-3", className)}
      style={resolveReportSpacingStyle(spacing)}
      {...(scrollTargetKey != null
        ? reportScrollTargetProps(scrollTargetKey)
        : undefined)}
    >
      <SectionHeader
        title={title}
        backgroundColor={backgroundColor}
        labelTreatment={labelTreatment}
        labelStyle={labelStyle}
      />
      {caption ? <p className="m-0 text-xs text-gray-600">{caption}</p> : null}
      {articles.map((article, index) => (
        <ArticleItem
          // biome-ignore lint/suspicious/noArrayIndexKey: catalog articles have no stable id
          key={index}
          headline={article.headline}
          sourceName={article.sourceName}
          sourceUrl={article.sourceUrl}
          body={article.body}
          headlineStyle={headlineStyle}
          bodyStyle={bodyStyle}
        />
      ))}
    </section>
  );
};
